import React, { useState, useEffect } from 'react';
import api from '../services/api';

const Optimisation = ({ clientId }) => {
  const [analyse, setAnalyse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exercice, setExercice] = useState(new Date().getFullYear());

  useEffect(() => {
    if (clientId) {
      loadAnalyse();
    }
  }, [clientId, exercice]);

  const loadAnalyse = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/optimisation/${clientId}?exercice=${exercice}`);
      if (response.success && response.analyse) {
        setAnalyse(response.analyse);
      }
    } catch (err) {
      console.error('Erreur chargement optimisation:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px'}}>
        <div style={{textAlign: 'center'}}>
          <div style={{width: '40px', height: '40px', border: '3px solid #1D9E75', borderTopColor: 'transparent', borderRadius: '50%', margin: '0 auto 16px', animation: 'spin 0.8s linear infinite'}}></div>
          <p style={{color: '#73726C', fontSize: '14px'}}>Chargement...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!analyse) {
    return (
      <div style={{padding: '24px', textAlign: 'center'}}>
        <div style={{fontSize: '48px', marginBottom: '16px'}}>📊</div>
        <h3 style={{fontSize: '18px', fontWeight: '600', color: '#2D2D2D', marginBottom: '8px'}}>
          Analyse non disponible
        </h3>
        <p style={{fontSize: '14px', color: '#73726C'}}>
          Veuillez générer des documents fiscaux pour accéder à l'analyse d'optimisation.
        </p>
      </div>
    );
  }

  const getPositionColor = (position) => {
    switch (position) {
      case 'excellent': return '#1D9E75';
      case 'bon': return '#1D9E75';
      case 'moyen': return '#F59E0B';
      case 'alerte': return '#DC2626';
      default: return '#73726C';
    }
  };

  const getPositionLabel = (position) => {
    switch (position) {
      case 'excellent': return 'Excellent';
      case 'bon': return 'Bon';
      case 'moyen': return 'Moyen';
      case 'alerte': return 'À surveiller';
      default: return position;
    }
  };

  return (
    <div style={{padding: '24px', maxWidth: '1200px', margin: '0 auto'}}>
      {/* Header */}
      <div style={{marginBottom: '32px'}}>
        <h2 style={{fontSize: '24px', fontWeight: '600', color: '#2D2D2D', marginBottom: '8px'}}>
          📊 Optimisation Fiscale & Benchmarking
        </h2>
        <p style={{fontSize: '14px', color: '#73726C'}}>
          Comparez vos ratios aux standards sectoriels Banque de France
        </p>
      </div>

      {/* Sélecteur d'exercice */}
      <div style={{background: 'white', padding: '16px', borderRadius: '12px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <div style={{display: 'flex', alignItems: 'center', gap: '16px'}}>
          <label style={{fontSize: '14px', fontWeight: '500', color: '#2D2D2D'}}>Exercice :</label>
          <select
            value={exercice}
            onChange={(e) => setExercice(parseInt(e.target.value))}
            style={{padding: '8px 12px', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '14px'}}
          >
            {Array.from({ length: 5 }, (_, i) => {
              const year = new Date().getFullYear() - i;
              return <option key={year} value={year}>{year}</option>;
            })}
          </select>
        </div>
        <div style={{fontSize: '12px', color: '#73726C', background: '#FAF9F5', padding: '6px 12px', borderRadius: '8px'}}>
          Secteur : {analyse.secteur?.replace('_', ' ').toUpperCase() || 'Non défini'}
        </div>
      </div>

      {/* Positionnement vs secteur */}
      <div style={{background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)'}}>
        <h3 style={{fontSize: '18px', fontWeight: '600', color: '#2D2D2D', marginBottom: '16px'}}>
          Positionnement vs secteur
        </h3>

        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px'}}>
          {Object.entries(analyse.positionnement || {}).map(([ratio, data]) => (
            <div
              key={ratio}
              style={{
                border: '1px solid #E8E7E3',
                borderRadius: '8px',
                padding: '16px',
                background: '#FAF9F5'
              }}
            >
              <div style={{fontSize: '13px', fontWeight: '500', color: '#73726C', marginBottom: '8px', textTransform: 'uppercase', fontSize: '11px'}}>
                {ratio.replace(/_/g, ' ')}
              </div>

              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px'}}>
                <span style={{fontSize: '24px', fontWeight: '600', color: '#2D2D2D'}}>
                  {data.valeur_client?.toFixed(1)}{ratio.includes('pct') ? '%' : ' jours'}
                </span>
                <span style={{
                  fontSize: '12px',
                  fontWeight: '500',
                  color: 'white',
                  background: getPositionColor(data.position),
                  padding: '4px 8px',
                  borderRadius: '12px'
                }}>
                  {getPositionLabel(data.position)}
                </span>
              </div>

              <div style={{fontSize: '12px', color: '#73726C', marginBottom: '4px'}}>
                Médiane secteur : {data.mediane_secteur?.toFixed(1)}{ratio.includes('pct') ? '%' : ' jours'}
              </div>

              <div style={{fontSize: '12px', color: data.ecart_vs_mediane < 0 ? '#1D9E75' : '#DC2626'}}>
                Écart : {data.ecart_vs_mediane > 0 ? '+' : ''}{data.ecart_vs_mediane}%
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Conseils d'optimisation */}
      {analyse.conseils && analyse.conseils.length > 0 && (
        <div style={{background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)'}}>
          <h3 style={{fontSize: '18px', fontWeight: '600', color: '#2D2D2D', marginBottom: '16px'}}>
            💡 Conseils d'optimisation
          </h3>

          <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
            {analyse.conseils.map((conseil, idx) => (
              <div
                key={idx}
                style={{
                  border: '1px solid #E8E7E3',
                  borderRadius: '8px',
                  padding: '16px',
                  background: idx === 0 ? '#E1F5EE' : '#FAF9F5'
                }}
              >
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px'}}>
                  <h4 style={{fontSize: '15px', fontWeight: '600', color: '#2D2D2D', flex: 1}}>
                    {conseil.titre}
                  </h4>
                  <span style={{
                    fontSize: '11px',
                    fontWeight: '500',
                    color: 'white',
                    background: conseil.priorite === 'haute' ? '#DC2626' : conseil.priorite === 'moyenne' ? '#F59E0B' : '#1D9E75',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    textTransform: 'uppercase'
                  }}>
                    {conseil.priorite}
                  </span>
                </div>

                <p style={{fontSize: '13px', color: '#2D2D2D', lineHeight: '1.5', marginBottom: '12px'}}>
                  {conseil.description}
                </p>

                <div style={{display: 'flex', gap: '24px', fontSize: '12px', color: '#73726C'}}>
                  <div>
                    <span style={{fontWeight: '500'}}>Levier :</span> {conseil.levier}
                  </div>
                  <div>
                    <span style={{fontWeight: '500'}}>Impact estimé :</span> +{conseil.impact_ca_pct}% du CA
                  </div>
                </div>

                <div style={{background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: '6px', padding: '10px', marginTop: '8px'}}>
                  <div style={{fontSize: '12px', fontWeight: '500', color: '#92400E', marginBottom: '4px'}}>
                    Action à faire dans les 30 jours :
                  </div>
                  <div style={{fontSize: '13px', color: '#2D2D2D'}}>
                    {conseil.action_30j}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Avertissement */}
      <div style={{background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: '8px', padding: '12px', marginTop: '24px'}}>
        <div style={{display: 'flex', gap: '8px', fontSize: '12px', color: '#92400E'}}>
          <span>⚠️</span>
          <div>
            <strong style={{color: '#92400E'}}>Recommandations informatives</strong> — Ces conseils sont basés sur des comparaisons sectorielles et doivent être validés par votre expert-comptable avant toute mise en œuvre.
          </div>
        </div>
      </div>
    </div>
  );
};

export default Optimisation;
